/**
 * Admin dashboard — HR-focused tenant-wide view.
 *
 * Per the client revamp, all timesheet/hours surfaces moved to the
 * standalone Timesheet dashboard (/admin/timesheets). What lives here:
 *   • Team progress (this month) — tickets / points / hours / wiki / PRs / commits
 *   • Team leave activity — leave requests across the tenant
 *
 * Admin-only. Backend re-enforces via `admin.view_team_timesheet` /
 * `admin.view_team_leaves`.
 */

import { useQuery } from "@tanstack/react-query";
import { type CSSProperties } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Avatar } from "@m2s/design-system";

import { DesktopOnly } from "@/components/DesktopOnly";
import { api } from "@/lib/api";
import type { AdminLeaveSummary, TeamProgress } from "@/lib/types";
import { useAuthStore } from "@/store/auth";

const ADMIN_ROLES = new Set(["admin"]);

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function AdminDashboardPage() {
  // The team-progress table is wide (11 columns) — gate to desktop.
  return (
    <DesktopOnly
      title="The HR dashboard"
      blurb="The team-progress table doesn't fit a phone screen. Use this view on a wider window."
    >
      <AdminDashboardPageImpl />
    </DesktopOnly>
  );
}

function AdminDashboardPageImpl() {
  const navigate = useNavigate();
  const memberships = useAuthStore((s) => s.memberships);
  const isAdmin = memberships.some((m) => ADMIN_ROLES.has(m.role));

  const progressQuery = useQuery({
    queryKey: ["admin-team-progress"],
    queryFn: async () => (await api.get<TeamProgress>("/admin/team-progress")).data,
    enabled: isAdmin,
  });

  const leavesQuery = useQuery({
    queryKey: ["admin", "team-leaves"],
    queryFn: async () => (await api.get<AdminLeaveSummary[]>("/admin/team-leaves")).data,
    enabled: isAdmin,
    retry: false,
  });

  if (!isAdmin) {
    return (
      <div style={{ maxWidth: 480, margin: "120px auto", textAlign: "center" }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: "0 0 12px" }}>Admin only</h1>
        <p style={{ color: "var(--body)", lineHeight: 1.6 }}>
          This dashboard is visible to workspace admins. Ask your admin if you need access.
        </p>
        <button
          type="button"
          onClick={() => navigate("/")}
          style={{
            marginTop: 16,
            background: "var(--ink)",
            color: "var(--on-primary)",
            border: "none",
            borderRadius: 4,
            padding: "8px 14px",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Back to workspace
        </button>
      </div>
    );
  }

  return (
    <div>
      <header style={headerRow}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
            Admin dashboard
          </h1>
          <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
            Team progress and leave activity. For hours logged across the team, see the{" "}
            <Link to="/admin/timesheets" style={{ color: "var(--ink)" }}>
              Timesheet dashboard
            </Link>
            .
          </p>
        </div>
      </header>

      <section style={{ marginTop: 8 }}>
        <div style={sectionTitle}>Team progress · this month</div>
        {progressQuery.isLoading ? (
          <p style={{ color: "var(--mute)" }}>Loading…</p>
        ) : progressQuery.data && progressQuery.data.rows.length > 0 ? (
          <div
            style={{
              border: "1px solid var(--hairline)",
              borderRadius: 8,
              background: "var(--canvas)",
              overflowX: "auto",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                minWidth: 900,
              }}
            >
              <thead>
                <tr style={{ background: "var(--surface-1)" }}>
                  <th style={progTh}>Member</th>
                  <th style={progTh}>Role</th>
                  <th style={progThNum}>Tasks done</th>
                  <th style={progThNum}>Points</th>
                  <th style={progThNum}>Hours</th>
                  <th style={progThNum}>Docs</th>
                  <th style={progThNum}>Revisions</th>
                  <th style={progThNum}>PRs</th>
                  <th style={progThNum}>Merged</th>
                  <th style={progThNum}>Commits</th>
                  <th style={progTh}>GitHub</th>
                </tr>
              </thead>
              <tbody>
                {progressQuery.data.rows.map((r) => (
                  <tr key={r.user_id} style={{ borderTop: "1px solid var(--hairline)" }}>
                    <td style={progTd}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <Avatar initials={initialsOf(r.display_name)} size={20} />
                        <span style={{ color: "var(--ink)" }}>{r.display_name}</span>
                      </span>
                      <div style={{ fontSize: 11, color: "var(--mute)" }}>{r.email}</div>
                    </td>
                    <td style={progTd}>
                      <span
                        style={{
                          fontSize: 11,
                          fontFamily: "var(--font-mono)",
                          background: "var(--surface-2)",
                          padding: "1px 6px",
                          borderRadius: 3,
                          color: "var(--body-mid)",
                        }}
                      >
                        {r.role}
                      </span>
                    </td>
                    <td style={progTdNum}>{r.tickets_done}</td>
                    <td style={progTdNum}>{r.story_points_done}</td>
                    <td style={progTdNum}>{r.hours_logged}</td>
                    <td style={progTdNum}>{r.pages_authored}</td>
                    <td style={progTdNum}>{r.revisions_made}</td>
                    <td style={progTdNum}>{r.prs_raised}</td>
                    <td style={progTdNum}>{r.prs_merged}</td>
                    <td style={progTdNum}>{r.commits}</td>
                    <td style={progTd}>
                      {r.github_connected ? (
                        <span style={{ fontSize: 11, color: "#047a18" }}>● connected</span>
                      ) : (
                        <span style={{ fontSize: 11, color: "var(--mute)" }}>— not connected</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: "var(--mute)" }}>No activity yet this month.</p>
        )}
      </section>

      <section style={{ marginTop: 32 }}>
        <div style={sectionTitle}>Team leave activity</div>
        {leavesQuery.isLoading ? (
          <p style={{ color: "var(--mute)" }}>Loading…</p>
        ) : leavesQuery.data && leavesQuery.data.length > 0 ? (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              background: "var(--canvas)",
              fontSize: 13.5,
            }}
          >
            <thead>
              <tr>
                {["Member", "Type", "From", "To", "Days", "Status"].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: "left",
                      padding: "10px 8px",
                      borderBottom: "1px solid var(--hairline)",
                      fontSize: 11,
                      fontWeight: 500,
                      letterSpacing: 0.6,
                      textTransform: "uppercase",
                      color: "var(--mute)",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leavesQuery.data.map((r, i) => (
                <tr key={`${r.user_id}-${i}`}>
                  <td style={cellBase}>{r.user_display_name}</td>
                  <td style={cellBase}>{r.leave_type_name}</td>
                  <td style={cellBase}>{r.starts_on}</td>
                  <td style={cellBase}>{r.ends_on}</td>
                  <td style={{ ...cellBase, fontFamily: "var(--font-mono)" }}>{r.total_days}</td>
                  <td style={cellBase}>
                    <span style={leaveStatusPill(r.status)}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: "var(--mute)" }}>No leave requests on file.</p>
        )}
      </section>
    </div>
  );
}

// ---------- styles ----------

const headerRow: CSSProperties = {
  marginBottom: 16,
  display: "flex",
  alignItems: "baseline",
  gap: 16,
};

const sectionTitle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 8,
};

const progTh: CSSProperties = {
  textAlign: "left",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  padding: "10px 12px",
  borderBottom: "1px solid var(--hairline)",
};

const progThNum: CSSProperties = { ...progTh, textAlign: "right" };

const progTd: CSSProperties = {
  padding: "10px 12px",
  fontSize: 13,
  color: "var(--body)",
  verticalAlign: "middle",
};

const progTdNum: CSSProperties = {
  ...progTd,
  textAlign: "right",
  fontFamily: "var(--font-mono)",
  fontSize: 12.5,
  color: "var(--ink)",
};

const cellBase: CSSProperties = {
  borderBottom: "1px solid var(--hairline)",
  height: 36,
  padding: "0 8px",
};

function leaveStatusPill(status: string): CSSProperties {
  return {
    fontSize: 12,
    padding: "2px 8px",
    borderRadius: 2,
    background:
      status === "approved"
        ? "rgba(0,215,34,0.12)"
        : status === "rejected"
          ? "rgba(238,29,54,0.12)"
          : "var(--surface-2)",
    color:
      status === "approved"
        ? "#047a18"
        : status === "rejected"
          ? "#ee1d36"
          : "var(--body-mid)",
  };
}
